import type { PerformancePerson } from '@gokrew/models/performance';
import type { TFunction } from 'i18next';
import { isAxiosError } from 'axios';
import {
  ReportingErrorCode,
  type ReportingPerson,
  type TemporaryGroup,
  type TemporaryGroupChangedDetails,
  type TemporaryGroupMember,
} from '@gokrew/models/reporting';
import { getNameFromObj } from '@gokrew/ui/utility/utility';

export const personName = (person: ReportingPerson) => getNameFromObj(person);

/**
 * `PersonBadge` (avatar + name + title) is shared with performance and takes
 * its person shape.
 */
export const toBadgePerson = (person: ReportingPerson): PerformancePerson => ({
  id: person.employeeId,
  userId: '',
  firstName: person.firstName,
  lastName: person.lastName,
  email: null,
  profileImage: person.profileImage,
  designation: person.designation,
  department: null,
});

/** A temporary group member as `PersonBadge` takes it. */
export const memberBadgePerson = (
  member: TemporaryGroupMember
): PerformancePerson => ({
  id: member.employeeId,
  userId: '',
  firstName: member.firstName,
  lastName: member.lastName,
  email: null,
  profileImage: member.profileImage,
  designation: member.jobTitle,
  department: null,
});

/** "Job title · Branch", or null when neither is known. */
export const titleAndBranch = (
  jobTitle: string | null | undefined,
  branch: string | null | undefined
) => [jobTitle, branch].filter(Boolean).join(' · ') || null;

/**
 * A temporary group's name: "Rick's former reports", or for lines set as
 * Temporary by hand (D55) "Temporary reports of Vera". Employee namespace.
 */
export const temporaryGroupName = (t: TFunction, group: TemporaryGroup) =>
  group.formerManager
    ? t('reporting_line.groups.former_reports', {
        former: group.formerManager.name,
      })
    : t('reporting_line.groups.manual_reports', {
        cover: group.coveringManager.name,
      });

/**
 * The group's current members from a 409 `reporting.groupChanged` (the group
 * changed while its move dialog was open), or null for any other error.
 */
export const groupChangedDetails = (
  error: unknown
): TemporaryGroupChangedDetails | null => {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data as
    | { errorCode?: string; errorDetails?: TemporaryGroupChangedDetails }
    | undefined;
  return body?.errorCode === ReportingErrorCode.GroupChanged &&
    body.errorDetails
    ? body.errorDetails
    : null;
};
